import React, { useState } from 'react';
import TmuaPearsonShell, {
  DoorIcon,
  NextIcon,
  TmuaPearsonFooterButton,
  useTmuaPearsonAccessKeys,
  isLnatPearsonExam,
  isEsatPearsonExam,
  isTaraPearsonExam,
  isUcatPearsonExam,
  getPearsonPlayerExamTitle,
} from './TmuaPearsonShell';
import TmuaPearsonDialog from './TmuaPearsonDialog';
import { TMUA_PEARSON_TOKENS } from '../constants/tmuaPearsonTokens';
import { getPearsonColorSchemeDropdownLabel } from '../constants/tmuaColorSchemes';

const TmuaModeSelectScreen = ({
  examName,
  title,
  examHeading,
  tsaSitting = false,
  heritageExam,
  modeChoice,
  onSelectMode,
  showWarning,
  onDismissWarning,
  onNext,
  onEndExam,
  colorScheme = 'pearsonDefault',
  setColorScheme,
}) => {
  const [showEndExam, setShowEndExam] = useState(false);
  const T = TMUA_PEARSON_TOKENS;
  const isLnat = isLnatPearsonExam(examName);
  const isEsat = isEsatPearsonExam(examName);
  const isTara = isTaraPearsonExam(examName);
  const isUcat = isUcatPearsonExam(examName);
  const nextVariant = isLnat || isEsat || isTara || isUcat ? 'chrome' : 'yellow';
  const nextIconColor = isLnat || isEsat || isTara || isUcat ? '#FFFFFF' : T.nextDefaultFg;

  useTmuaPearsonAccessKeys(
    {
      showEndExamModalVisible: showEndExam,
      showResponseRequiredVisible: Boolean(showWarning),
      nextAvailable: !showEndExam && !showWarning,
      endExamAvailable: !showWarning,
    },
    {
      onNext: () => { if (!showWarning && !showEndExam) onNext(); },
      onEndExam: () => setShowEndExam(true),
      onConfirmYes: () => { if (showEndExam) onEndExam(); },
      onConfirmNo: () => setShowEndExam(false),
      onDismissOk: () => { if (showWarning) onDismissWarning(); },
    }
  );

  return (
    <TmuaPearsonShell
      examName={isLnat || isEsat || isTara || isUcat ? examName : 'TMUA'}
      title={title || getPearsonPlayerExamTitle(examName, undefined, { tsaSitting, heritageExam })}
      tsaSitting={tsaSitting}
      heritageExam={heritageExam}
      colorSchemeLabel={(isLnat || isUcat) ? getPearsonColorSchemeDropdownLabel(examName) : undefined}
      onBackToSelection={onEndExam}
      colorScheme={colorScheme}
      setColorScheme={setColorScheme}
      footerLeft={(
        <TmuaPearsonFooterButton onClick={() => setShowEndExam(true)} icon={<DoorIcon color={T.titleBarFg} />} underline="e" keepLabelOnMobile>
          End Exam
        </TmuaPearsonFooterButton>
      )}
      footerRight={(
        <TmuaPearsonFooterButton variant={nextVariant} onClick={onNext} icon={<NextIcon color={nextIconColor} />} iconPosition="end" underline="n">
          Next
        </TmuaPearsonFooterButton>
      )}
    >
      <div style={{ position: 'absolute', inset: 0, overflowY: 'auto', padding: '18px 22px', color: '#000', fontFamily: T.fontUi, fontSize: 16, lineHeight: 1.35 }}>
        <div style={{ width: '100%', textAlign: 'left' }}>
          <p style={{ margin: '0 0 10px', color: 'var(--iv-body)' }}>
            Welcome to the <strong>{examHeading || getPearsonPlayerExamTitle(examName, undefined, { tsaSitting, heritageExam })}</strong>.
          </p>
          <p style={{ margin: '0 0 10px', color: '#000' }}>
            This practice sitting is designed to help you familiarise yourself with the test format, question style, and
            the navigation features of the test system.
          </p>
          <p style={{ margin: '0 0 10px', color: 'var(--iv-body)' }}>
            There are {isUcat ? 'three modes' : 'two modes'} for this sample test:
          </p>
          <p style={{ margin: '0 0 10px', color: 'var(--iv-body)' }}>
            1. <strong>Timed Mode:</strong> This test uses the standard time limit. The test will automatically end
            when the time runs out.
          </p>
          {isUcat ? (
            <p style={{ margin: '0 0 10px', color: 'var(--iv-body)' }}>
              2. <strong>Timed Mode (25% Extra Time):</strong> This test uses the standard time limit plus an extra
              25%. The test will automatically end when the time runs out.
            </p>
          ) : null}
          <p style={{ margin: '0 0 10px', color: 'var(--iv-body)' }}>
            {isUcat ? '3' : '2'}. <strong>Untimed Mode:</strong> This test does not have a time limit. The test will end once you have
            completed all the questions at your own pace.
          </p>
          <p style={{ margin: '0 0 10px', color: 'var(--iv-body)' }}>
            Select your preferred mode:
          </p>
          <div style={{ marginBottom: 12 }}>
            {(isUcat ? ['timed', 'timed_extra', 'untimed'] : ['timed', 'untimed']).map((mode, idx) => (
              <label
                key={mode}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', cursor: 'pointer', color: 'var(--iv-body)' }}
              >
                <input
                  type="radio"
                  name="tmua-mode"
                  checked={modeChoice === mode}
                  onChange={() => onSelectMode(mode)}
                />
                <span style={{ fontWeight: 700, fontSize: 16 }}>
                  {idx + 1}. {mode === 'timed' ? 'Timed mode' : mode === 'timed_extra' ? 'Timed mode (25% Extra Time)' : 'Untimed mode'}
                </span>
              </label>
            ))}
          </div>
          <p style={{ margin: 0, color: 'var(--iv-body)' }}>
            Click the <strong>Next (N)</strong> button after making your selection to continue.
          </p>
        </div>
      </div>
      {showWarning ? (
        <TmuaPearsonDialog title="Response Required" onOk={onDismissWarning}>
          You cannot continue with this question unanswered.
        </TmuaPearsonDialog>
      ) : null}
      {showEndExam ? (
        <TmuaPearsonDialog
          title="End Exam"
          info
          onYes={onEndExam}
          onNo={() => setShowEndExam(false)}
        >
          You have chosen to end this exam. Are you sure you want to end this exam?
        </TmuaPearsonDialog>
      ) : null}
    </TmuaPearsonShell>
  );
};

export default TmuaModeSelectScreen;
